import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Wrench,
  ShieldCheck,
  Globe2,
  Sparkles,
  Package,
  Search,
  ClipboardCheck,
  Truck,
} from "lucide-react";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { BrandsStrip } from "@/components/BrandsStrip";
import { GearAnimation } from "@/components/GearAnimation";
import aboutHero from "@/assets/about-hero.jpg";

const CANONICAL = "https://fixhen.com/about";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About FIXHEN — European-made classic Mercedes & BMW parts" },
      {
        name: "description",
        content:
          "FIXHEN has crafted OEM-grade parts for modern classic Mercedes-Benz and BMW since 2023. Made in Europe, shipped worldwide from Sofia, Bulgaria.",
      },
      { property: "og:title", content: "About FIXHEN — Built in Europe, shipped worldwide" },
      {
        property: "og:description",
        content:
          "A workshop obsessed with modern-classic Mercedes and BMW. Made in Europe since 2023.",
      },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `https://fixhen.com${aboutHero}` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: `https://fixhen.com${aboutHero}` },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "AboutPage",
              url: CANONICAL,
              name: "About FIXHEN",
              description:
                "The story behind FIXHEN — European-made parts for classic Mercedes-Benz and BMW.",
            },
            {
              "@type": "Organization",
              name: "FIXHEN",
              url: "https://fixhen.com/",
              foundingDate: "2023",
              areaServed: "Worldwide",
              email: "info@contact.fixhen.com",
              telephone: "+359 88 775 2171",
              address: {
                "@type": "PostalAddress",
                streetAddress: "6 Prof. Dr Dimitar Dobrev St.",
                addressLocality: "Sofia",
                addressCountry: "BG",
              },
              contactPoint: [{
                "@type": "ContactPoint",
                telephone: "+359 88 775 2171",
                email: "info@contact.fixhen.com",
                contactType: "customer support",
                areaServed: "Worldwide",
                availableLanguage: ["English", "Bulgarian"],
              }],
            },
          ],
        }),
      },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <div className="flex min-h-screen flex-col bg-background text-ink">
      <AnnouncementBar />
      <SiteHeader />

      <main>
        <Hero />
        <Stats />
        <Story />
        <Values />
        <Process />
        <Models />
        <Cta />
      </main>

      <SiteFooter />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-border">
      <div className="pointer-events-none absolute -right-24 -top-24 opacity-10">
        <GearAnimation />
      </div>
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 md:grid-cols-2 md:items-center md:py-24">
        <div>
          <p className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-ink-soft">
            <Sparkles className="h-3.5 w-3.5 text-royal" /> Since 2023 · Sofia, Bulgaria
          </p>
          <h1 className="mt-5 text-4xl font-black leading-[1.05] tracking-tight text-ink sm:text-5xl md:text-6xl">
            Parts with a pedigree,{" "}
            <span className="bg-gradient-to-r from-ink to-sky-pop bg-clip-text text-transparent">
              engineered in Europe.
            </span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-ink-soft sm:text-lg">
            FIXHEN builds and curates OEM-grade parts for the modern classics we grew up loving —
            the Mercedes R129, W124, W201, W208 and the BMW E30 through E46. Every component is made
            in Europe, quality-checked by hand, and shipped worldwide from our workshop in Sofia.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/shop"
              className="press inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-ink px-6 text-sm font-semibold text-paper hover:bg-ink/90"
            >
              Shop the catalogue <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              className="press inline-flex h-12 items-center justify-center rounded-xl border border-border bg-background px-6 text-sm font-semibold text-ink hover:bg-secondary"
            >
              Talk to a specialist
            </Link>
          </div>
        </div>
        <div className="relative">
          <div className="overflow-hidden rounded-3xl border border-border shadow-2xl">
            <img
              src={aboutHero}
              alt="Classic Mercedes-Benz W124 inside the FIXHEN workshop"
              width={1600}
              height={1000}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 hidden max-w-xs rounded-2xl border border-border bg-background/95 p-4 shadow-xl backdrop-blur md:block">
            <p className="text-xs font-semibold uppercase tracking-widest text-royal">Est. 2023</p>
            <p className="mt-1 text-sm text-ink-soft">
              A workshop obsessed with fitment, materials, and the details other suppliers skip.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const items = [
    { k: "2023", v: "Founded in Sofia, Bulgaria" },
    { k: "100%", v: "Made in Europe" },
    { k: "150+", v: "Countries shipped to" },
    { k: "9", v: "Chassis families supported" },
  ];
  return (
    <section className="border-b border-border bg-secondary/40">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-4 py-10 sm:px-6 md:grid-cols-4 md:py-14">
        {items.map((s) => (
          <div key={s.k}>
            <div className="text-3xl font-black text-ink sm:text-4xl">{s.k}</div>
            <div className="mt-1 text-xs font-medium uppercase tracking-wider text-ink-soft sm:text-sm">
              {s.v}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Story() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24">
      <div className="grid gap-10 md:grid-cols-5 md:gap-16">
        <div className="md:col-span-2">
          <p className="text-xs font-semibold uppercase tracking-widest text-royal">Our story</p>
          <h2 className="mt-3 text-3xl font-black tracking-tight text-ink sm:text-4xl">
            Started in a garage. Powered by obsession.
          </h2>
        </div>
        <div className="space-y-5 text-base leading-relaxed text-ink-soft md:col-span-3">
          <p>
            FIXHEN began in 2023, when a small team of Mercedes-Benz and BMW enthusiasts got tired of
            hunting down obsolete parts of unpredictable quality. We started making — and sourcing —
            components ourselves, from European suppliers we trust, then testing every batch on our
            own cars before it ever touches a customer's.
          </p>
          <p>
            Today, restoring modern classics is our full-time craft. Every order is picked and packed
            in our Sofia workshop, and every part is backed by a fitment promise: if it doesn't fit,
            we make it right.
          </p>
          <blockquote className="relative rounded-2xl border-l-4 border-sky-pop bg-secondary/60 px-6 py-5 text-lg font-medium italic text-ink">
            "We don't sell parts. We keep legends on the road."
            <footer className="mt-2 not-italic text-sm font-semibold text-ink-soft">
              — The FIXHEN team
            </footer>
          </blockquote>
        </div>
      </div>
    </section>
  );
}

function Values() {
  const items = [
    {
      icon: Wrench,
      title: "European craftsmanship",
      body:
        "Every part is manufactured in Europe by suppliers we audit in person. No mystery factories, no compromises.",
    },
    {
      icon: ShieldCheck,
      title: "Fitment you can trust",
      body:
        "Cross-referenced against original OEM numbers and test-fitted on our reference cars before it hits a shelf.",
    },
    {
      icon: Globe2,
      title: "Delivered worldwide",
      body:
        "Insured tracked shipping to 150+ countries, with EU customs handled and free delivery over €99.",
    },
  ];
  return (
    <section className="border-y border-border bg-secondary/40">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-20">
        <div className="mb-10 max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-royal">What we stand for</p>
          <h2 className="mt-3 text-3xl font-black tracking-tight text-ink sm:text-4xl">
            Three promises. No exceptions.
          </h2>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {items.map((v) => (
            <div
              key={v.title}
              className="group rounded-2xl border border-border bg-background p-6 transition-shadow hover:shadow-lg"
            >
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-ink text-paper transition-transform group-hover:-rotate-6">
                <v.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-lg font-bold text-ink">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-soft">{v.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Process() {
  const steps = [
    { icon: Search, title: "Source", body: "We identify OEM references and vet European manufacturers." },
    { icon: ClipboardCheck, title: "Inspect", body: "Every batch is dimensionally checked and test-fitted." },
    { icon: Package, title: "Pack", body: "Protective packaging, invoice, and torque specs included." },
    { icon: Truck, title: "Ship", body: "Insured tracked delivery — worldwide, with EU customs handled." },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24">
      <div className="mb-10 max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-royal">How we work</p>
        <h2 className="mt-3 text-3xl font-black tracking-tight text-ink sm:text-4xl">
          From workbench to your driveway.
        </h2>
      </div>
      <ol className="grid gap-5 md:grid-cols-4">
        {steps.map((s, i) => (
          <li key={s.title} className="relative rounded-2xl border border-border bg-background p-6">
            <div className="absolute -top-3 left-6 rounded-full bg-ink px-3 py-0.5 text-xs font-bold text-paper">
              Step {i + 1}
            </div>
            <s.icon className="mt-2 h-6 w-6 text-royal" />
            <h3 className="mt-4 text-lg font-bold text-ink">{s.title}</h3>
            <p className="mt-1 text-sm leading-relaxed text-ink-soft">{s.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

function Models() {
  return (
    <section className="border-t border-border bg-secondary/40">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-20">
        <div className="mb-10 max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-royal">Specialities</p>
          <h2 className="mt-3 text-3xl font-black tracking-tight text-ink sm:text-4xl">
            The chassis we live and breathe.
          </h2>
          <p className="mt-3 text-base text-ink-soft">
            Every catalogue entry is mapped to a real chassis code. If it's here, we've built it, driven it,
            or restored one ourselves.
          </p>
        </div>
        <BrandsStrip />
      </div>
    </section>
  );
}

function Cta() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24">
      <div className="overflow-hidden rounded-3xl bg-ink px-8 py-12 text-paper sm:px-14 sm:py-16">
        <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-center">
          <div>
            <h2 className="text-3xl font-black tracking-tight sm:text-4xl">
              Can't find your part? Talk to a human.
            </h2>
            <p className="mt-3 max-w-2xl text-base text-paper/80">
              Send us your chassis number and OEM reference — we'll source it from Europe and ship it
              anywhere. No bots, no scripts, just enthusiasts on the other end.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="press inline-flex h-12 items-center justify-center rounded-xl bg-paper px-6 text-sm font-semibold text-ink hover:bg-paper/90"
            >
              Contact FIXHEN <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <a
              href="mailto:info@contact.fixhen.com"
              className="press inline-flex h-12 items-center justify-center rounded-xl border border-white/20 px-6 text-sm font-semibold text-paper hover:bg-white/5"
            >
              info@contact.fixhen.com
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}